/**
 * Discovery Service - Autonomous pattern and target discovery
 * Actively discovers patterns from chat and explores internet for APIs
 */

import type { LLMMessage } from "./llmService";
import { convexService } from "./convexService";
import type { DiscoveredPattern } from "./convexService";

export interface DiscoveryTarget {
  id: string;
  type: "api" | "website" | "data_source" | "service";
  name: string;
  url: string;
  description: string;
  discovered: Date;
  explored: boolean;
  capabilities: string[];
  confidence: number;
}

export interface DiscoveredPatternLocal {
  id: string;
  intent: string;
  triggerPhrases: string[];
  suggestedOperators: string[];
  confidence: number;
  source: "chat" | "web" | "inference";
  discovered: Date;
  saved: boolean;
  sourceUrl?: string;
  relatedPatterns?: string[];
}

export interface DiscoveryStats {
  patternsDiscovered: number;
  targetsFound: number;
  apisExplored: number;
  lastDiscoveryRun: Date | null;
  isRunning: boolean;
}

export interface DiscoveryEvent {
  type: "pattern_discovered" | "target_found" | "exploration_complete" | "error" | "status_change";
  data: unknown;
  timestamp: Date;
}

class DiscoveryService {
  private compartmentId: string = "";
  private isRunning = false;
  private discoveryInterval: ReturnType<typeof setInterval> | null = null;
  private discoveredPatterns: DiscoveredPatternLocal[] = [];
  private discoveredTargets: DiscoveryTarget[] = [];
  private chatHistory: Array<{ role: string; content: string }> = [];

  // Known API discovery sources
  private readonly API_DISCOVERY_SOURCES = [
    "https://api.apis.guru/v2/list.json",
    "https://raw.githubusercontent.com/public-apis/public-apis/master/README.md",
  ];

  private eventCallbacks: ((event: DiscoveryEvent) => void)[] = [];

  // Pattern extraction prompt
  private readonly PATTERN_EXTRACTION_PROMPT = `You are an intent pattern extractor for an AGI system. Analyze user messages and extract:
1. Distinct user intents/goals
2. Common trigger phrases that indicate each intent
3. What operations/APIs would be needed to fulfill each intent

Respond in JSON format:
{
  "patterns": [
    {
      "intent": "string - brief description of intent",
      "triggerPhrases": ["array of phrases that trigger this intent"],
      "suggestedOperators": ["array of operation names like fetch_weather, search_code, etc."],
      "confidence": 0.0-1.0
    }
  ]
}

Only extract clear, actionable patterns. Be concise.`;

  // Web exploration prompt
  private readonly WEB_EXPLORATION_PROMPT = `You are an API/service discovery agent for an AGI system. Given a topic or capability need, suggest:
1. Public APIs that could provide this capability
2. Data sources that could be useful
3. Services that could be integrated

Respond in JSON format:
{
  "targets": [
    {
      "type": "api|website|data_source|service",
      "name": "string",
      "url": "string - actual URL if known, or 'search:{query}' if needs lookup",
      "description": "what this provides",
      "capabilities": ["list of capabilities"]
    }
  ]
}

Focus on free, public, and well-documented APIs. Be specific.`;

  /**
   * Start discovery engine
   */
  async start(intervalMs: number = 30000): Promise<void> {
    if (this.isRunning) return;

    this.isRunning = true;
    this.emit("status_change", { running: true });

    // Get compartment ID
    await this.ensureCompartment();

    // Initial discovery run
    await this.runDiscoveryCycle();

    // Set up periodic discovery
    this.discoveryInterval = setInterval(() => {
      this.runDiscoveryCycle().catch((err) => {
        console.error("Discovery error:", err);
        this.emit("error", { message: String(err) });
      });
    }, intervalMs);
  }

  /**
   * Stop discovery engine
   */
  stop(): void {
    if (this.discoveryInterval) {
      clearInterval(this.discoveryInterval);
      this.discoveryInterval = null;
    }
    this.isRunning = false;
    this.emit("status_change", { running: false });
  }

  /**
   * Subscribe to discovery events
   */
  onEvent(callback: (event: DiscoveryEvent) => void): () => void {
    this.eventCallbacks.push(callback);
    return () => {
      this.eventCallbacks = this.eventCallbacks.filter((cb) => cb !== callback);
    };
  }

  /**
   * Emit discovery event
   */
  private emit(type: DiscoveryEvent["type"], data: unknown): void {
    const event: DiscoveryEvent = { type, data, timestamp: new Date() };
    this.eventCallbacks.forEach((cb) => cb(event));
  }

  /**
   * Add chat message for pattern analysis
   */
  addChatMessage(role: string, content: string): void {
    this.chatHistory.push({ role, content });

    // Keep last 50 messages
    if (this.chatHistory.length > 50) {
      this.chatHistory = this.chatHistory.slice(-50);
    }

    // Auto-trigger discovery after every 3 user messages
    const userMessageCount = this.chatHistory.filter((m) => m.role === "user").length;
    if (role === "user" && userMessageCount % 3 === 0 && userMessageCount > 0) {
      console.log("🔍 Auto-triggering pattern discovery after", userMessageCount, "user messages");
      this.runDiscoveryCycle().catch((err) => {
        console.error("Auto-discovery error:", err);
      });
    }
  }

  /**
   * Get discovery statistics
   */
  getStats(): DiscoveryStats {
    return {
      patternsDiscovered: this.discoveredPatterns.length,
      targetsFound: this.discoveredTargets.length,
      apisExplored: this.discoveredTargets.filter((t) => t.explored).length,
      lastDiscoveryRun: this.discoveredPatterns[0]?.discovered || null,
      isRunning: this.isRunning,
    };
  }

  /**
   * Get discovered patterns
   */
  getDiscoveredPatterns(): DiscoveredPatternLocal[] {
    return [...this.discoveredPatterns];
  }

  /**
   * Get discovered targets
   */
  getDiscoveredTargets(): DiscoveryTarget[] {
    return [...this.discoveredTargets];
  }

  /**
   * Run full discovery cycle
   */
  private async runDiscoveryCycle(): Promise<void> {
    try {
      console.log("🧠 Discovery Cycle Starting...", {
        chatMessages: this.chatHistory.filter((m) => m.role === "user").length,
        existingPatterns: this.discoveredPatterns.length,
        existingTargets: this.discoveredTargets.length,
      });

      // 1. Extract patterns from chat history
      if (this.chatHistory.length > 0) {
        await this.extractPatternsFromChatLLM();
      } else {
        console.log("📝 No chat history yet");
      }

      // 2. Explore internet for APIs
      await this.exploreInternet();

      // 3. Auto-save high-confidence patterns
      await this.autoSavePatterns();

      console.log("✨ Discovery Cycle Complete:", {
        patterns: this.discoveredPatterns.length,
        targets: this.discoveredTargets.length,
        operators: this.discoveredTargets.filter((t) => t.explored).length,
      });

      this.emit("exploration_complete", {
        patterns: this.discoveredPatterns.length,
        targets: this.discoveredTargets.length,
        operatorsCreated: this.discoveredTargets.filter((t) => t.explored).length,
      });
    } catch (error) {
      console.error("Discovery cycle error:", error);
      this.emit("error", { message: String(error) });
    }
  }

  /**
   * Extract patterns from chat using LLM
   */
  private async extractPatternsFromChatLLM(): Promise<void> {
    // Import dynamically to avoid circular dependency
    const { useDashboardStore } = await import("@/store");
    const state = useDashboardStore.getState();

    const userMessages = this.chatHistory
      .filter((m) => m.role === "user")
      .map((m) => m.content)
      .join("\n---\n");

    if (!userMessages.trim()) {
      console.log("📝 No user messages for pattern extraction");
      return;
    }

    // Import LLM service
    const { getLLMConfigs, callLLM } = await import("./llmService");

    const configs = getLLMConfigs(state.settings);
    if (configs.length === 0) {
      console.warn("⚠️ No LLM configured for pattern extraction");
      return;
    }

    const messages: LLMMessage[] = [
      { role: "system", content: this.PATTERN_EXTRACTION_PROMPT },
      { role: "user", content: `Analyze these user messages:\n\n${userMessages}` },
    ];

    try {
      const response = await callLLM(messages, configs[0]);
      const json = this.extractJSON(response.content);

      if (json?.patterns && Array.isArray(json.patterns)) {
        let newPatterns = 0;
        for (const p of json.patterns as Array<{
          intent?: string;
          triggerPhrases?: string[];
          suggestedOperators?: string[];
          confidence?: number;
        }>) {
          const exists = this.discoveredPatterns.some(
            (dp) => dp.intent.toLowerCase() === p.intent?.toLowerCase()
          );

          const confidence = p.confidence ?? 0;

          if (!exists && p.intent && confidence >= 0.4) {
            const pattern: DiscoveredPatternLocal = {
              id: `pat_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
              intent: p.intent,
              triggerPhrases: p.triggerPhrases || [],
              suggestedOperators: p.suggestedOperators || [],
              confidence,
              source: "chat",
              discovered: new Date(),
              saved: false,
            };

            this.discoveredPatterns.push(pattern);
            this.emit("pattern_discovered", pattern);
            newPatterns++;
          }
        }
        console.log(`✨ Extracted ${newPatterns} new patterns`);
      }
    } catch (error) {
      console.error("❌ Pattern extraction error:", error);
    }
  }

  /**
   * Explore internet for APIs
   */
  private async exploreInternet(): Promise<void> {
    // Fetch from public API directory
    await this.fetchPublicAPIs();

    // Use LLM to suggest APIs for patterns
    await this.discoverAPIsForPatterns();
  }

  /**
   * Fetch public APIs from apis.guru
   */
  private async fetchPublicAPIs(): Promise<void> {
    try {
      const apiUrl = this.API_DISCOVERY_SOURCES[0];
      if (!apiUrl) return;

      const response = await fetch(apiUrl);
      if (!response.ok) return;

      const data = await response.json() as Record<string, any>;
      const apiKeys = Object.keys(data);

      // Sample random APIs
      const sample = apiKeys.sort(() => Math.random() - 0.5).slice(0, 10);
      let addedCount = 0;

      for (const apiKey of sample) {
        const apiInfo = data[apiKey];
        if (!apiInfo) continue;

        const preferredVersion = apiInfo.preferred;
        const versionInfo = apiInfo.versions?.[preferredVersion];
        const info = versionInfo?.info;

        if (!info) continue;

        const apiName = info.title || apiKey.split(":")[0] || apiKey;
        const apiUrl = info.contact?.url ||
                       info["x-origin"]?.[0]?.url ||
                       versionInfo.swaggerUrl ||
                       `https://api.apis.guru/v2/${apiKey}`;

        const exists = this.discoveredTargets.some(
          (t) => t.url === apiUrl || t.name === apiName
        );

        if (!exists) {
          const categories = info["x-categories"] || [];
          const target: DiscoveryTarget = {
            id: `tgt_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
            type: "api",
            name: apiName,
            url: apiUrl,
            description: info.description || `API: ${apiName}`,
            discovered: new Date(),
            explored: false,
            capabilities: categories.length > 0 ? categories : ["api"],
            confidence: 0.75,
          };

          this.discoveredTargets.push(target);
          this.emit("target_found", target);
          addedCount++;
        }
      }

      console.log(`🌐 Discovered ${addedCount} new APIs`);
    } catch (error) {
      console.log("Public API fetch skipped:", error);
    }
  }

  /**
   * Use LLM to discover APIs for patterns
   */
  private async discoverAPIsForPatterns(): Promise<void> {
    // Import LLM service
    const { getLLMConfigs, callLLM } = await import("./llmService");
    const { useDashboardStore } = await import("@/store");
    const state = useDashboardStore.getState();

    const configs = getLLMConfigs(state.settings);
    if (configs.length === 0) return;

    const patternsNeedingAPIs = this.discoveredPatterns
      .filter((p) => p.suggestedOperators.length > 0 && !p.saved)
      .slice(0, 3);

    if (patternsNeedingAPIs.length === 0) return;

    const needs = patternsNeedingAPIs
      .map((p) => `- ${p.intent}: needs ${p.suggestedOperators.join(", ")}`)
      .join("\n");

    const messages: LLMMessage[] = [
      { role: "system", content: this.WEB_EXPLORATION_PROMPT },
      { role: "user", content: `Find APIs/services for these capabilities:\n\n${needs}` },
    ];

    try {
      const response = await callLLM(messages, configs[0]);
      const json = this.extractJSON(response.content);

      if (json?.targets && Array.isArray(json.targets)) {
        for (const t of json.targets as Array<{
          type?: string;
          name?: string;
          url?: string;
          description?: string;
          capabilities?: string[];
        }>) {
          const exists = this.discoveredTargets.some(
            (dt) => dt.name.toLowerCase() === t.name?.toLowerCase()
          );

          if (!exists && t.name && t.url) {
            const targetType = (t.type === "api" || t.type === "website" || t.type === "data_source" || t.type === "service")
              ? t.type
              : "api" as const;
            const target: DiscoveryTarget = {
              id: `tgt_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
              type: targetType,
              name: t.name,
              url: t.url,
              description: t.description || "",
              discovered: new Date(),
              explored: false,
              capabilities: t.capabilities || [],
              confidence: 0.6,
            };

            this.discoveredTargets.push(target);
            this.emit("target_found", target);
          }
        }
      }
    } catch (error) {
      console.error("API discovery error:", error);
    }
  }

  /**
   * Auto-save high-confidence patterns
   */
  private async autoSavePatterns(): Promise<void> {
    if (!this.compartmentId) {
      await this.ensureCompartment();
      if (!this.compartmentId) {
        console.error("❌ No compartmentId, cannot save patterns");
        return;
      }
    }

    const patternsToSave = this.discoveredPatterns.filter(
      (p) => !p.saved && p.confidence >= 0.6
    );

    for (const pattern of patternsToSave) {
      try {
        await convexService.mutate("discoveredPatterns:create", {
          compartmentId: this.compartmentId,
          source: pattern.source,
          intent: pattern.intent,
          triggerPhrases: pattern.triggerPhrases,
          suggestedOperators: pattern.suggestedOperators,
          confidence: pattern.confidence,
          sourceUrl: pattern.sourceUrl,
          relatedPatterns: pattern.relatedPatterns,
        });

        pattern.saved = true;
        console.log(`✅ Auto-saved pattern: ${pattern.intent}`);
      } catch (error) {
        console.error(`Failed to save pattern ${pattern.intent}:`, error);
      }
    }
  }

  /**
   * Ensure compartment exists
   */
  private async ensureCompartment(): Promise<void> {
    if (this.compartmentId) return;

    try {
      const compartments = await convexService.query("compartments:list", {});
      if (compartments.length > 0) {
        this.compartmentId = compartments[0]._id;
      } else {
        const id = await convexService.mutate("compartments:create", {
          name: "Discovery",
          description: "Auto-discovered patterns and operators",
          limits: {
            maxOperators: 100,
            maxPatterns: 1000,
            maxRequestsPerMinute: 100,
          },
        }) as string;
        this.compartmentId = id;
      }
    } catch (error) {
      console.error("Failed to get/create compartment:", error);
    }
  }

  /**
   * Extract JSON from text
   */
  private extractJSON(text: string): Record<string, unknown> | null {
    try {
      const jsonMatch = text.match(/\{[\s\S]*\}/);
      if (jsonMatch) {
        return JSON.parse(jsonMatch[0]);
      }
    } catch {
      // Ignore parse errors
    }
    return null;
  }

  /**
   * Manually trigger discovery
   */
  async discoverNow(): Promise<void> {
    await this.runDiscoveryCycle();
  }

  /**
   * Save specific pattern
   */
  async savePattern(patternId: string): Promise<void> {
    const pattern = this.discoveredPatterns.find((p) => p.id === patternId);
    if (!pattern || pattern.saved) return;

    await this.ensureCompartment();
    if (!this.compartmentId) return;

    await convexService.mutate("discoveredPatterns:create", {
      compartmentId: this.compartmentId,
      source: pattern.source,
      intent: pattern.intent,
      triggerPhrases: pattern.triggerPhrases,
      suggestedOperators: pattern.suggestedOperators,
      confidence: pattern.confidence,
      sourceUrl: pattern.sourceUrl,
      relatedPatterns: pattern.relatedPatterns,
    });

    pattern.saved = true;
    console.log(`✅ Saved pattern: ${pattern.intent}`);
  }

  /**
   * Create operator from target
   */
  async createOperatorFromTarget(targetId: string): Promise<string | null> {
    const target = this.discoveredTargets.find((t) => t.id === targetId);
    if (!target) {
      console.error(`❌ Target not found: ${targetId}`);
      return null;
    }

    await this.ensureCompartment();
    if (!this.compartmentId) {
      console.error("❌ No compartmentId available");
      return null;
    }

    try {
      const operatorId = await convexService.mutate("operators:create", {
        compartmentId: this.compartmentId,
        name: target.name,
        description: target.description || `Auto-discovered ${target.type}: ${target.name}`,
        endpoint: target.url,
        method: "GET",
        authType: "none",
        timeout: 30000,
      }) as string;

      target.explored = true;
      console.log(`✅ Created operator: ${target.name} (${operatorId})`);
      this.emit("status_change", { operatorCreated: target.name, operatorId });

      return operatorId;
    } catch (error) {
      console.error(`❌ Failed to create operator from ${target.name}:`, error);
      this.emit("error", {
        message: `Failed to create operator: ${String(error)}`,
        target: target.name,
      });
      return null;
    }
  }
}

export const discoveryService = new DiscoveryService();
